import { z } from "zod";
import type { ShareVoiceSidebarCategory } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { SIDEBAR_FILTER_CATEGORIES } from "@/lib/constants/filter-categories";

/**
 * Common URL parameter types used across multiple pages (News Feed, Share of Voice)
 * These types define the structure for URL parameter synchronization in the application
 */

/**
 * Common period types used across multiple pages (News Feed, Share of Voice).
 * Chart aggregation: day / week / month only.
 */
export type PeriodType = "day" | "week" | "month" | null;

// =============================================================================
// TANSTACK ROUTER SEARCH SCHEMAS
// =============================================================================

const periodSchema = z.enum(["day", "week", "month"]).nullable();

/** How multiple key-message selections combine (same values as `FilterExclusion` in `@/types/shared`). */
const keyMessageConjunctionSchema = z.enum(["AND", "OR"]).optional();

const filterCategorySchema = z.enum([...SIDEBAR_FILTER_CATEGORIES] as [
  string,
  ...string[],
]);

const supportedFilterCategories = new Set<string>(SIDEBAR_FILTER_CATEGORIES);

const sanitizeSelectedFilters = (selected: unknown): unknown => {
  if (
    selected === null ||
    typeof selected !== "object" ||
    Array.isArray(selected)
  ) {
    return selected;
  }

  return Object.fromEntries(
    Object.entries(selected).filter(([category]) =>
      supportedFilterCategories.has(category),
    ),
  );
};

/** Selected filters record shape (shared by all route schemas). Keys are optional (URL may omit categories). */
const selectedFiltersRecord = z.preprocess(
  sanitizeSelectedFilters,
  z.partialRecord(filterCategorySchema, z.array(z.string())),
);

/**
 * Builds TanStack Router search schema with a given default for `selected`.
 * Used to keep Discovery/Share of Voice and Analysis in sync on shape while differing on default.
 */
const createTanStackRouteSearchSchema = (
  selectedDefault: Record<string, string[]>,
) =>
  z.object({
    /** Number of days to filter (e.g., 7, 30, 90) */
    filterDays: z.number().default(30),
    /** Custom start date in YYYY-MM-DD format */
    startDate: z.string().optional(),
    /** Custom end date in YYYY-MM-DD format */
    endDate: z.string().optional(),
    /** Period for chart aggregation */
    period: periodSchema.default("day"),
    /** Selected filters by category (UPPERCASE keys). */
    selected: selectedFiltersRecord.default(selectedDefault),
    /** Active saved view id for UI restore; not a filter category. */
    savedViewId: z.string().optional(),
    /**
     * Key message AND/OR when multiple `KEY_MESSAGE` ids are in `selected`.
     * Analysis: omitted means use key-message store; when set, URL wins for queries. Store stays in sync with navigates/toggles.
     */
    keyMessageConjunction: keyMessageConjunctionSchema,
  });

/**
 * Search schema for Discovery and Share of Voice routes.
 * No default category; empty selection when no params present.
 */
export const tanStackRouteSearchSchema = createTanStackRouteSearchSchema({});

/**
 * Search schema for Analysis route only.
 * Defaults to News category when no selection params present.
 */
export const tanStackRouteSearchSchemaAnalysis =
  createTanStackRouteSearchSchema({ CATEGORY: ["NEWS"] });

/**
 * TanStack Router search params type (inferred from schema; same shape for all routes).
 */
export type TanStackRouteSearch = z.infer<typeof tanStackRouteSearchSchema>;

/**
 * Common date range parameters used across multiple pages
 * @interface DateRangeParams
 */
export interface DateRangeParams {
  /** Number of days to filter by */
  filterDays: number | undefined;
  /** Whether custom date range is enabled */
  isCustomDaysRange: boolean;
  /** Custom start date (when isCustomDaysRange is true) */
  startDate?: Date;
  /** Custom end date (when isCustomDaysRange is true) */
  endDate?: Date;
  /** Period for data aggregation */
  period: PeriodType;
}

/**
 * Common filter selection parameters used across multiple pages
 * @interface FilterSelectionParams
 */
export interface FilterSelectionParams {
  /** Map of selected filters by category */
  selectedFilters: Map<ShareVoiceSidebarCategory, string[]>;
}

/**
 * Common URL parameters slice interface used across multiple pages
 * @interface UrlParamsSlice
 */
export type UrlParamsSlice = DateRangeParams & FilterSelectionParams;

/**
 * Common URL parameter actions used across multiple pages
 * @interface UrlParamsActions
 */
export interface UrlParamsActions {
  /** Sets the period for data aggregation */
  setPeriod: (period: PeriodType) => void;
  /** Sets the number of filter days */
  setFilterDays: (days: number) => void;
  /** Sets custom start and end dates */
  setCustomDateRange: (startDate: Date, endDate: Date) => void;
  /** Clears all state and resets to defaults */
  clearState: () => void;
}

/**
 * Common filter selection actions used across multiple pages
 * @interface FilterSelectionActions
 */
export interface FilterSelectionActions {
  /** Toggles selection of a filter item for a specific category */
  toggleFilterSelection: (
    selection: ShareVoiceSidebarCategory,
    item: string | number,
  ) => void;
}
